import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Plus, Edit, Users, RefreshCw, X } from 'lucide-react';
import { permisosApi, RolConPermisos } from '../../apis/permisosApi';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const NIVELES = [
  { value: 1, label: 'Nivel 1 - Básico' },
  { value: 2, label: 'Nivel 2 - Intermedio' },
  { value: 3, label: 'Nivel 3 - Avanzado' },
];

const Roles: React.FC = () => {
  const queryClient = useQueryClient();
  const { nivel: miNivel } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingRol, setEditingRol] = useState<RolConPermisos | null>(null);
  const [form, setForm] = useState({ nombre: '', descripcion: '', nivel: 1 });

  const { data: roles = [], isLoading } = useQuery<RolConPermisos[]>({
    queryKey: ['roles'],
    queryFn: permisosApi.getRoles,
  });

  const saveMutation = useMutation({
    mutationFn: () => editingRol
      ? permisosApi.editarRol(editingRol.id, form)
      : permisosApi.crearRol(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(editingRol ? 'Rol actualizado' : 'Rol creado');
      closeForm();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Error al guardar'),
  });

  const openCreate = () => {
    setEditingRol(null);
    setForm({ nombre: '', descripcion: '', nivel: 1 });
    setShowForm(true);
  };

  const openEdit = (rol: RolConPermisos) => {
    setEditingRol(rol);
    setForm({ nombre: rol.nombre, descripcion: rol.descripcion || '', nivel: rol.nivel });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRol(null);
  };

  const getNivelColor = (nivel: number) => {
    if (nivel >= 4) return '#FF9500';
    if (nivel >= 3) return '#007AFF';
    if (nivel >= 2) return '#34C759';
    return '#8E8E93';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-300">Cargando roles...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .roles-container { min-height: 100vh; background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%); color: white; padding: 2rem; }
        .content-wrapper { max-width: 1400px; margin: 0 auto; }
        .page-title { font-size: 2.5rem; font-weight: 700; background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 0.5rem; }
        .page-subtitle { color: rgba(255,255,255,0.7); font-size: 1.1rem; margin-bottom: 2rem; }
        .controls-section { background: rgba(255,255,255,0.05); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 1.5rem; margin-bottom: 2rem; display: flex; justify-content: space-between; align-items: center; }
        .btn { padding: 12px 20px; border-radius: 12px; font-weight: 500; display: inline-flex; align-items: center; gap: 8px; border: none; cursor: pointer; font-size: 1rem; transition: all 0.3s ease; }
        .btn-primary { background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%); color: white; }
        .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,122,255,0.3); }
        .btn-secondary { background: rgba(255,255,255,0.1); color: white; border: 1px solid rgba(255,255,255,0.2); }
        .btn-secondary:hover { background: rgba(255,255,255,0.2); }
        .roles-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1.5rem; }
        .role-card { background: rgba(255,255,255,0.05); backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 1.5rem; transition: all 0.3s ease; }
        .role-card:hover { background: rgba(255,255,255,0.08); border-color: rgba(0,122,255,0.3); transform: translateY(-2px); }
        .role-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
        .role-name { font-size: 1.25rem; font-weight: 700; color: white; }
        .role-desc { color: rgba(255,255,255,0.6); font-size: 0.9rem; margin-bottom: 1rem; }
        .role-meta { display: flex; gap: 1rem; align-items: center; }
        .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; border: 1px solid; }
        .action-btn { padding: 8px; border-radius: 8px; border: none; background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.7); cursor: pointer; transition: all 0.3s; display: inline-flex; }
        .action-btn:hover { background: rgba(255,255,255,0.2); color: white; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 2rem; width: 100%; max-width: 500px; }
        .form-group { margin-bottom: 1.25rem; }
        .form-label { display: block; color: rgba(255,255,255,0.8); font-size: 0.9rem; font-weight: 500; margin-bottom: 0.5rem; }
        .form-input, .form-select { width: 100%; padding: 12px 16px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 12px; color: white; font-size: 1rem; }
        .form-input:focus, .form-select:focus { outline: none; border-color: #007AFF; box-shadow: 0 0 0 3px rgba(0,122,255,0.1); }
        .form-select option { background: #1a1a1a; color: white; }
        .form-actions { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem; }
      `}} />

      <div className="roles-container">
        <div className="content-wrapper">
          <h1 className="page-title">Gestión de Roles</h1>
          <p className="page-subtitle">Administra los roles del sistema y sus niveles de acceso</p>

          <div className="controls-section">
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>{roles.length} roles configurados</span>
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /> Crear Rol
            </button>
          </div>

          <div className="roles-grid">
            {roles.map(rol => (
              <div key={rol.id} className="role-card">
                <div className="role-header">
                  <div>
                    <div className="role-name">{rol.nombre}</div>
                  </div>
                  {rol.nivel < miNivel && (
                    <button className="action-btn" onClick={() => openEdit(rol)} title="Editar rol">
                      <Edit size={16} />
                    </button>
                  )}
                </div>
                <div className="role-desc">{rol.descripcion || 'Sin descripción'}</div>
                <div className="role-meta">
                  <span className="badge" style={{
                    background: `${getNivelColor(rol.nivel)}20`,
                    color: getNivelColor(rol.nivel),
                    borderColor: `${getNivelColor(rol.nivel)}40`,
                  }}>
                    <Shield size={12} /> Nivel {rol.nivel}
                  </span>
                  <span className="badge" style={{
                    background: 'rgba(0,122,255,0.2)', color: '#007AFF', borderColor: 'rgba(0,122,255,0.3)',
                  }}>
                    <Users size={12} /> {rol.usuarios_count || 0} usuarios
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#007AFF' }}>
                {editingRol ? 'Editar Rol' : 'Crear Rol'}
              </h2>
              <button className="action-btn" onClick={closeForm}><X size={20} /></button>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input className="form-input" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre del rol" />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <input className="form-input" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Descripción del rol" />
            </div>
            <div className="form-group">
              <label className="form-label">Nivel de acceso</label>
              <select className="form-select" value={form.nivel} onChange={e => setForm({ ...form, nivel: parseInt(e.target.value) })}>
                {NIVELES.filter(n => n.value < miNivel).map(n => (
                  <option key={n.value} value={n.value}>{n.label}</option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={closeForm}>Cancelar</button>
              <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={!form.nombre || saveMutation.isPending}>
                {saveMutation.isPending ? <RefreshCw size={16} className="animate-spin" /> : null}
                {editingRol ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Roles;
